import { ref, get, push, update, remove, DataSnapshot } from 'firebase/database';
import { database } from './core';
import logger from '../../utils/logger';

export interface CustomMetric {
  title: string;
  isTracked: boolean;
  value?: number;
  dateCreated?: number;
}

/**
 * Filter metrics from Firebase to structured objects
 */
const filterMetrics = (
  snapshot: DataSnapshot,
  isSuggested = false
): { 
  customMetrics?: Record<string, CustomMetric>,
  suggestedMetrics?: Record<string, CustomMetric>,
  sortedCustomMetrics?: string[],
  sortedSuggestedMetrics?: string[],
  activeMetrics?: string[]
} => {
  const keys = isSuggested 
    ? ['suggestedMetrics', 'sortedSuggestedMetrics'] 
    : ['customMetrics', 'sortedCustomMetrics'];
  
  const output: any = {
    [keys[0]]: {},
    [keys[1]]: [],
  };
  
  if (!isSuggested) {
    output.activeMetrics = [];
  }
  
  if (!snapshot.exists()) {
    return output;
  }
  
  // Process each metric
  snapshot.forEach((metric) => {
    const value = metric.val() as CustomMetric;
    const key = metric.key as string;
    
    output[keys[0]][key] = value;
    output[keys[1]].push(key);
    
    if (!isSuggested && value.isTracked) {
      output.activeMetrics.push(key);
    }
  });
  
  return output;
};

/**
 * Get user's custom metrics
 */
export const getUserCustomMetrics = async (userId: string): Promise<{
  customMetrics: Record<string, CustomMetric>;
  sortedCustomMetrics: string[];
  activeMetrics: string[];
}> => {
  try {
    const metricsRef = ref(database, `customMetrics/private/${userId}`);
    const snapshot = await get(metricsRef);
    return filterMetrics(snapshot) as {
      customMetrics: Record<string, CustomMetric>;
      sortedCustomMetrics: string[];
      activeMetrics: string[];
    };
  } catch (error) {
    logger.error('Error fetching user custom metrics:', error);
    throw error;
  }
};

/**
 * Get suggested metrics from public collection
 */
export const getCustomMetricsSuggestions = async (): Promise<{
  suggestedMetrics: Record<string, CustomMetric>;
  sortedSuggestedMetrics: string[];
}> => {
  try {
    const suggestionsRef = ref(database, 'customMetrics/public/suggestedMetrics');
    const snapshot = await get(suggestionsRef);
    return filterMetrics(snapshot, true) as {
      suggestedMetrics: Record<string, CustomMetric>;
      sortedSuggestedMetrics: string[];
    };
  } catch (error) {
    logger.error('Error fetching custom metrics suggestions:', error);
    throw error;
  }
};

/**
 * Add a new custom metric for the user
 */
export const addCustomMetric = async (userId: string, newMetric: CustomMetric): Promise<string> => {
  try {
    const userMetricsRef = ref(database, `customMetrics/private/${userId}`);
    const metricToAdd = {
      ...newMetric,
      dateCreated: Date.now()
    };
    
    const newRef = push(userMetricsRef, metricToAdd);
    if (!newRef.key) {
      throw new Error('Failed to generate key for new metric');
    }
    
    return newRef.key;
  } catch (error) {
    logger.error('Error adding custom metric:', error);
    throw error;
  }
};

/**
 * Update an existing custom metric
 */
export const updateCustomMetric = async (
  userId: string,
  metricId: string,
  updatedData: Partial<CustomMetric>
): Promise<void> => {
  try {
    const metricRef = ref(database, `customMetrics/private/${userId}/${metricId}`);
    await update(metricRef, updatedData);
  } catch (error) {
    logger.error('Error updating custom metric:', error);
    throw error;
  }
};

/**
 * Delete a custom metric
 */
export const deleteCustomMetric = async (userId: string, metricId: string): Promise<void> => {
  try {
    const metricRef = ref(database, `customMetrics/private/${userId}/${metricId}`);
    await remove(metricRef);
  } catch (error) {
    logger.error('Error deleting custom metric:', error);
    throw error;
  }
};